import React, { useState, useEffect } from "react";

export default function TaskCard({ task, onEdit, onDelete }) {
  const [timeLeft, setTimeLeft] = useState("");
  const [isExpired, setIsExpired] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const isDone = task.status === "Done";

  useEffect(() => {
    const updateTick = () => {
      const now = new Date();
      setCurrentTime(now);
      if (isDone || !task.date) return;

      const target = new Date(task.date);
      const diffInMs = target - now;

      if (diffInMs <= 0) {
        setIsExpired(true);
        setTimeLeft("EXPIRED");
        return;
      }

      setIsExpired(false);
      const mins = Math.floor(diffInMs / (1000 * 60));
      const hours = Math.floor(diffInMs / (1000 * 60 * 60));
      const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (days >= 1) {
        setTimeLeft(`${days} ${days === 1 ? "day" : "days"} left`);
      } else if (hours >= 1) {
        setTimeLeft(`${hours} ${hours === 1 ? "hour" : "hours"} left`);
      } else {
        setTimeLeft(`${mins} ${mins === 1 ? "minute" : "minutes"} left`);
      }
    };

    updateTick();
    const interval = setInterval(updateTick, 1000);
    return () => clearInterval(interval);
  }, [task.date, isDone]);

  return (
    <div className="group bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
      <div
        className={`absolute top-0 left-0 w-2 h-full ${
          isDone ? "bg-emerald-500" : isExpired ? "bg-red-500" : "bg-amber-400"
        }`}
      ></div>

      <div className="flex justify-between items-start mb-5">
        <div className="flex flex-col gap-2">
          <span
            className={`w-fit px-3 py-1 rounded-lg text-[10px] font-black uppercase ${
              isDone
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600"
                : "bg-amber-100 dark:bg-amber-900/30 text-amber-600"
            }`}
          >
            {task.status}
          </span>
          {!isDone && (
            <span
              className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md ${
                isExpired
                  ? "text-red-500 bg-red-50 dark:bg-red-900/20"
                  : "text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
              }`}
            >
              <i
                className={
                  isExpired
                    ? "fas fa-skull-crossbones"
                    : "fas fa-clock animate-pulse"
                }
              ></i>
              {timeLeft}
            </span>
          )}
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => onEdit(task)}
            className="text-slate-300 hover:text-indigo-500"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            onClick={() => onDelete(task.id)}
            className="text-slate-300 hover:text-red-500"
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>

      <h3 className="text-xl font-bold mb-4 dark:text-white line-clamp-2 h-14 leading-tight">
        {task.title}
      </h3>

      <div className="flex justify-between items-end border-t border-slate-50 dark:border-slate-800 pt-4 mt-2">
        <div className="text-slate-400 text-[11px] font-bold flex items-center gap-2">
          <i className="far fa-calendar-alt text-indigo-500"></i>
          {task.date
            ? new Date(task.date).toLocaleString([], {
                dateStyle: "medium",
                timeStyle: "short",
              })
            : "No Date"}
        </div>
        <div className="bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-lg text-[9px] font-mono text-slate-400 border dark:border-slate-700">
          {currentTime.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
}
